/**
 * 企业治理 E · E-HEALTH INDEX 企业健康度
 *
 * 产品目标：让企业家第一次用「健康体检」的语言看自己的企业，而不是用财务报表。
 *
 * 与原型的差别：
 *   - 总分、五维权重、二级指标、CHBS、关键人风险全部来自 GET /api/enterprise/overview；
 *   - 二级指标数量已确认为 19 项（早期文档写 18 属源资料内部矛盾），本页一律用
 *     subs.length 实时统计，界面上不出现任何写死的数量；
 *   - CHBS 的分值/比率/金额在接口层已数值化并带 unit，本页只做「同单位合计」，
 *     绝不把分值与金额相加，并在栏头写明合计口径；
 *   - 三螺旋传导模拟全部由 POST /api/enterprise/simulate 演算，前端只提交两个参数，
 *     拖动滑块时做 300ms 防抖，避免刷屏式请求。
 */
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ChbsItem, EnterpriseOverview, KeyPerson, SimulationInput, SimulationResult } from '@hew3/shared';
import { resolveColor } from '@hew3/shared';
import { ApiError, api } from '../../lib/api.ts';
import { charts } from '../../lib/charts.ts';
import { formatDate, formatMoney, formatPct, signed } from '../../lib/format.ts';
import { useUi } from '../../store/ui.ts';
import { AlertStrip, Badge, Card, Chart, Kpi, ListItem, StateBox } from '../../components/ui.tsx';

/* ------------------------------------------------------------------ *
 * 模拟器参数区间（与后端 zod 校验区间一致）与防抖时长
 * ------------------------------------------------------------------ */

const INVEST_MIN = 0;
const INVEST_MAX = 100;
const COVERAGE_MIN = 0.8;
const COVERAGE_MAX = 2.4;
const DEBOUNCE_MS = 300;

/** 关键人风险等级 → 徽章色调与中文说明 */
const KEY_PERSON_LEVEL: Record<KeyPerson['level'], { tone: 'ok' | 'warn' | 'danger'; text: string }> = {
  ok: { tone: 'ok', text: '风险平稳' },
  warn: { tone: 'warn', text: '需要关注' },
  danger: { tone: 'danger', text: '存在缺口' },
};

/**
 * 50+ 用户可读性：设计系统的 .badge 默认 10.5px 偏小，
 * 这里统一放大到 11.5px（仍然复用 Badge 组件与 .badge 类，不改动设计系统本身）。
 */
function ReadableBadge({
  tone,
  children,
}: {
  tone: 'h' | 'e' | 'w' | 'ok' | 'warn' | 'danger' | 'ghost';
  children: ReactNode;
}) {
  return (
    <Badge tone={tone}>
      <span style={{ fontSize: 11.5 }}>{children}</span>
    </Badge>
  );
}

/** 同单位合计：只累加与 unit 一致的条目，杜绝「分值 + 金额」这类跨单位相加。 */
function sumByUnit(items: ChbsItem[], unit: ChbsItem['unit']): number {
  return items.reduce((total, item) => (item.unit === unit ? total + item.value : total), 0);
}

function countByUnit(items: ChbsItem[], unit: ChbsItem['unit']): number {
  return items.filter((item) => item.unit === unit).length;
}

function clampPct(value: number): number {
  return Math.max(0, Math.min(100, value));
}

/* ------------------------------------------------------------------ *
 * CHBS 单栏（健康资产 / 健康负债）
 * ------------------------------------------------------------------ */

function ChbsColumn({ title, tone, items }: { title: string; tone: 'asset' | 'liab'; items: ChbsItem[] }) {
  const moneyCount = countByUnit(items, 'cny');
  const scoreCount = countByUnit(items, 'score');
  const pctCount = countByUnit(items, 'pct');
  const moneyTotal = sumByUnit(items, 'cny');

  return (
    <div className={`bs-col ${tone}`}>
      <div className="bs-hd">
        <span>{title}</span>
        {moneyCount > 0 && <span className="num">{formatMoney(moneyTotal)}</span>}
      </div>

      {/* 栏头合计口径：只有同单位的项才可相加 */}
      <div style={{ fontSize: 11, color: 'var(--muted-2)', lineHeight: 1.6, marginBottom: 8 }}>
        合计口径：仅「¥ 金额」项（{moneyCount} 项）可相加
        <br />
        分值 {scoreCount} 项、比率 {pctCount} 项单位不同，不参与金额合计
      </div>

      {items.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--muted-2)' }}>本栏暂无计量条目。</div>
      ) : (
        items.map((item) => (
          <div className="bs-item" key={item.id}>
            <div className="bs-i-l" style={{ fontSize: 11.5 }}>
              {item.name}
            </div>
            <div className="bs-i-v" style={{ fontSize: 14 }}>
              {item.displayText}
            </div>
            <div className="bs-i-m" style={{ fontSize: 11 }}>
              计量方法：{item.method}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * 三螺旋传导模拟器
 * ------------------------------------------------------------------ */

function SpiralSimulator() {
  const showToast = useUi((s) => s.showToast);

  // 初始值取各自区间的中位，属于输入参数的默认值，不代表任何业务结论
  const [investIntensity, setInvestIntensity] = useState<number>((INVEST_MIN + INVEST_MAX) / 2);
  const [cashflowCoverage, setCashflowCoverage] = useState<number>((COVERAGE_MIN + COVERAGE_MAX) / 2);
  const [waiting, setWaiting] = useState(false);

  const simulate = useMutation({
    mutationFn: (input: SimulationInput) => api.post<SimulationResult>('/api/enterprise/simulate', input),
    onError: () => showToast('传导模拟演算失败，请稍后重试', 'error'),
  });
  const { mutate } = simulate;

  // 防抖：滑块停止 300ms 后才发起一次演算，拖动过程不刷屏
  useEffect(() => {
    setWaiting(true);
    const timer = window.setTimeout(() => {
      setWaiting(false);
      mutate({ investIntensity, cashflowCoverage });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [investIntensity, cashflowCoverage, mutate]);

  const result = simulate.data;
  const busy = waiting || simulate.isPending;

  return (
    <Card
      title="三螺旋传导模拟器"
      more={busy ? <span style={{ color: 'var(--h)' }}>演算中…</span> : <span>服务端演算</span>}
    >
      <div className="sim-ctl">
        <div className="sc-hd">
          <span>健康资本投入强度</span>
          <b>
            {investIntensity} / {INVEST_MAX}
          </b>
        </div>
        <input
          type="range"
          min={INVEST_MIN}
          max={INVEST_MAX}
          step={1}
          value={investIntensity}
          aria-label="健康资本投入强度，0 到 100"
          onChange={(e) => setInvestIntensity(Number(e.target.value))}
        />
        <div style={{ fontSize: 11, color: 'var(--muted-2)', marginTop: 6, lineHeight: 1.5 }}>
          0 = 不新增健康资本投入，{INVEST_MAX} = 满强度投入
        </div>
      </div>

      <div className="sim-ctl">
        <div className="sc-hd">
          <span>现金流覆盖率</span>
          <b>{cashflowCoverage.toFixed(2)} 倍</b>
        </div>
        <input
          type="range"
          min={COVERAGE_MIN}
          max={COVERAGE_MAX}
          step={0.01}
          value={cashflowCoverage}
          aria-label="现金流覆盖率，0.8 到 2.4 倍"
          onChange={(e) => setCashflowCoverage(Number(e.target.value))}
        />
        <div style={{ fontSize: 11, color: 'var(--muted-2)', marginTop: 6, lineHeight: 1.5 }}>
          {COVERAGE_MIN} 倍 = 覆盖偏紧，{COVERAGE_MAX} 倍 = 覆盖充裕
        </div>
      </div>

      {simulate.isError ? (
        <StateBox
          kind="error"
          title="演算请求失败"
          desc="传导模拟由服务端统一演算，请检查网络后重试。"
          action={
            <button
              type="button"
              className="btn sm"
              onClick={() => mutate({ investIntensity, cashflowCoverage })}
            >
              重新演算
            </button>
          }
        />
      ) : !result ? (
        <StateBox kind="loading" title="正在演算三螺旋传导路径" desc="首次进入会自动按当前参数演算一次。" />
      ) : (
        <>
          <div className="sim-out">
            <div className="so">
              <div className="so-l" style={{ fontSize: 11 }}>
                企业健康度
              </div>
              <div className="so-v">{result.enterpriseHealth}</div>
            </div>
            <div className="so">
              <div className="so-l" style={{ fontSize: 11 }}>
                市场风险敞口
              </div>
              <div className="so-v" style={{ color: resolveColor('danger') }}>
                {formatPct(result.marketRiskPct)}
              </div>
            </div>
            <div className="so">
              <div className="so-l" style={{ fontSize: 11 }}>
                财富健康等级
              </div>
              <div className="so-v" style={{ color: resolveColor('w') }}>
                {result.wealthGrade}
              </div>
            </div>
          </div>

          <div className="sp12" />

          <div className="sec-title" style={{ marginBottom: 8 }}>
            三年传导路径
            <small>由服务端按年度演算</small>
          </div>
          <Chart
            html={charts.chain({
              nodes: result.path.map((point) => ({
                icon: String(point.year),
                label: `第 ${point.year} 年`,
                value: `企业 ${point.enterprise}`,
                color: 'e',
                bg: 'rgba(91,146,255,.12)',
                bd: 'rgba(91,146,255,.28)',
              })),
            })}
          />

          <div style={{ marginTop: 10 }}>
            <div className="row" style={{ fontSize: 11, color: 'var(--muted-2)' }}>
              <span className="grow">年度</span>
              <span style={{ width: 56, textAlign: 'right' }}>生命健康</span>
              <span style={{ width: 56, textAlign: 'right' }}>企业健康</span>
              <span style={{ width: 56, textAlign: 'right' }}>财富健康</span>
            </div>
            {result.path.map((point) => (
              <div
                key={point.year}
                className="row"
                style={{ fontSize: 12.5, padding: '7px 0', borderTop: '1px solid var(--line)' }}
              >
                <span className="grow">第 {point.year} 年</span>
                <span className="num" style={{ width: 56, textAlign: 'right' }}>
                  {point.health}
                </span>
                <span className="num" style={{ width: 56, textAlign: 'right' }}>
                  {point.enterprise}
                </span>
                <span className="num" style={{ width: 56, textAlign: 'right' }}>
                  {point.wealth}
                </span>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 11, color: 'var(--muted-2)', marginTop: 8, lineHeight: 1.6 }}>
            本次演算参数（服务端回执）：投入强度 {result.input.investIntensity} / {INVEST_MAX}，现金流覆盖率{' '}
            {result.input.cashflowCoverage.toFixed(2)} 倍
          </div>

          <div className="note-src" style={{ marginTop: 12 }}>
            <b>模型依据</b>　{result.basis}
          </div>
          <div className="sec-note" style={{ fontSize: 11 }}>
            {result.disclaimer}
          </div>
        </>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * 页面
 * ------------------------------------------------------------------ */

export function EnterprisePage() {
  const navigate = useNavigate();
  const [openKey, setOpenKey] = useState<string | null>(null);

  const overviewQuery = useQuery({
    queryKey: ['enterprise'],
    queryFn: () => api.get<EnterpriseOverview>('/api/enterprise/overview'),
  });

  const head = (
    <div className="scr-head">
      <button type="button" className="back" aria-label="返回" onClick={() => navigate(-1)}>
        <svg viewBox="0 0 24 24">
          <path d="M15 5l-7 7 7 7" />
        </svg>
      </button>
      <div className="scr-title">
        企业治理 E<small>E-HEALTH INDEX 企业健康度</small>
      </div>
    </div>
  );

  if (overviewQuery.isLoading) {
    return (
      <div className="page">
        {head}
        <div className="skeleton skel-card" style={{ height: 180 }} />
        <div className="skeleton skel-card" style={{ height: 220 }} />
        <div className="skeleton skel-card" />
        <div className="skeleton skel-card" style={{ height: 260 }} />
      </div>
    );
  }

  if (overviewQuery.isError || !overviewQuery.data) {
    const reason = overviewQuery.error instanceof ApiError ? overviewQuery.error.message : '';
    return (
      <div className="page">
        {head}
        <StateBox
          kind="error"
          title="企业健康数据加载失败"
          desc={reason || '请检查网络后重试；若持续失败，可联系专属健康管家核对建档状态。'}
          action={
            <button type="button" className="btn sm" onClick={() => void overviewQuery.refetch()}>
              重新加载
            </button>
          }
        />
      </div>
    );
  }

  const data = overviewQuery.data;
  const { kpis, dimensions, chbs, keyPersons } = data;

  const totalSubs = dimensions.reduce((total, dimension) => total + dimension.subs.length, 0);
  const chbsItems = [...chbs.assets, ...chbs.liabilities];
  const dangerPersons = keyPersons.filter((person) => person.level === 'danger');

  if (dimensions.length === 0 && chbsItems.length === 0 && keyPersons.length === 0) {
    return (
      <div className="page">
        {head}
        <StateBox
          title="暂无企业健康档案"
          desc="接口已连通，但当前成员还没有可展示的五维指标、资产负债表与关键人清单，请联系专属健康管家完成建档。"
        />
      </div>
    );
  }

  return (
    <div className="page">
      {head}

      {/* ---------------- 总分卡 ---------------- */}
      <Card title="E-Health Index 企业健康度总分">
        <div className="row" style={{ gap: 14, alignItems: 'center' }}>
          <Chart
            html={charts.ring({ value: data.score, max: 100, size: 118, thickness: 10, color: 'e', label: 'E-HEALTH' })}
          />
          <div className="grow">
            <div className="row-flex" style={{ alignItems: 'baseline', gap: 8 }}>
              <span className="num" style={{ fontSize: 34, fontWeight: 700, lineHeight: 1, color: resolveColor('e') }}>
                {data.score}
              </span>
              <ReadableBadge tone="e">{data.grade}</ReadableBadge>
            </div>
            <div style={{ fontSize: 12.5, marginTop: 8, lineHeight: 1.6 }}>{data.gradeComment}</div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6, lineHeight: 1.6 }}>
              上次评估 {data.prevScore} 分 · 变化 {signed(data.score - data.prevScore)} 分
            </div>
          </div>
        </div>

        <div className="divider" />

        <div className="grid3">
          {kpis.map((kpi) => (
            <Kpi
              key={kpi.id}
              label={kpi.label}
              value={kpi.value}
              delta={
                <span style={{ fontSize: 11 }}>
                  {kpi.prevValue ? `${kpi.deltaText} · 上次 ${kpi.prevValue}` : kpi.deltaText}
                </span>
              }
            />
          ))}
        </div>

        <div className="note-src" style={{ marginTop: 12 }}>
          <b>数据来源</b>　{data.company} · 数据截至 {formatDate(data.measuredAt)}，由成员授权的经营与健康数据测算。
        </div>
      </Card>

      {/* ---------------- 五维一级指标 + 二级指标 ---------------- */}
      <Card
        title="五维一级指标"
        more={
          <span>
            {dimensions.length} 项一级 · {totalSubs} 项二级
          </span>
        }
      >
        {dimensions.length === 0 ? (
          <StateBox title="暂无维度数据" desc="尚未生成一级与二级指标，请联系专属健康管家补充评测。" />
        ) : (
          dimensions.map((dimension, index) => {
            const open = openKey === dimension.key;
            const subCount = dimension.subs.length;
            return (
              <div
                key={dimension.key}
                style={{ borderBottom: index < dimensions.length - 1 ? '1px solid var(--line)' : 'none' }}
              >
                <button
                  type="button"
                  className="eh-row"
                  aria-expanded={open}
                  onClick={() => setOpenKey(open ? null : dimension.key)}
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    padding: '11px 0',
                    textAlign: 'left',
                    color: 'inherit',
                    font: 'inherit',
                  }}
                >
                  <span className="eh-name" style={{ fontSize: 13 }}>
                    {dimension.name}
                    <s style={{ fontSize: 11 }}>权重 {dimension.weight}%</s>
                  </span>
                  <span className="eh-track">
                    <span
                      className="bar-fill"
                      style={{ display: 'block', width: `${clampPct(dimension.score)}%`, background: resolveColor('e') }}
                    />
                  </span>
                  <span className="eh-val">{dimension.score}</span>
                  <span style={{ fontSize: 11, color: 'var(--muted-2)', flex: '0 0 auto' }}>{open ? '▴' : '▾'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setOpenKey(open ? null : dimension.key)}
                  style={{
                    display: 'block',
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    padding: '0 0 10px',
                    textAlign: 'left',
                    color: 'var(--muted-2)',
                    fontSize: 11,
                    lineHeight: 1.5,
                  }}
                >
                  {open ? `收起 ${subCount} 项二级指标` : `点击展开 ${subCount} 项二级指标`}
                </button>

                {open &&
                  (subCount === 0 ? (
                    <div className="sec-note" style={{ fontSize: 11, paddingBottom: 12 }}>
                      该维度暂无二级指标记录。
                    </div>
                  ) : (
                    <div style={{ padding: '0 0 12px' }}>
                      <div className="sec-note" style={{ fontSize: 11, marginTop: 0 }}>
                        二级指标 · 共 {subCount} 项
                      </div>
                      {dimension.subs.map((sub) => (
                        <div className="row between" key={sub.id} style={{ gap: 10, padding: '7px 0' }}>
                          <div className="grow">
                            <div style={{ fontSize: 12.5 }}>{sub.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted-2)', marginTop: 2, lineHeight: 1.5 }}>
                              计量来源：{sub.source}
                            </div>
                          </div>
                          <div className="num" style={{ fontSize: 14, fontWeight: 600, color: resolveColor('e') }}>
                            {sub.value}
                            <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--muted)', marginLeft: 2 }}>分</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
              </div>
            );
          })
        )}
      </Card>

      {/* ---------------- 企业健康资产负债表 CHBS ---------------- */}
      <Card title="企业健康资产负债表 CHBS" more={<span>管理会计报表口径</span>}>
        <div className="bs-grid">
          <ChbsColumn title="健康资产" tone="asset" items={chbs.assets} />
          <ChbsColumn title="健康负债" tone="liab" items={chbs.liabilities} />
        </div>

        <div className="note-src" style={{ marginTop: 12 }}>
          <b>CHBS 口径</b>　{chbs.note}
        </div>
        <div className="sec-note" style={{ fontSize: 11 }}>
          栏头合计仅在同计量单位内进行：金额项与金额项相加，分值与比率各自独立展示，不做跨单位汇总。
        </div>
      </Card>

      {/* ---------------- 三螺旋传导模拟器 ---------------- */}
      <SpiralSimulator />

      {/* ---------------- 关键人健康风险 ---------------- */}
      <Card title="关键人健康风险" more={<span>{keyPersons.length} 位关键人</span>}>
        {keyPersons.length === 0 ? (
          <StateBox title="暂无关键人清单" desc="尚未录入关键岗位，请联系专属健康管家补充组织结构信息。" />
        ) : (
          <>
            {dangerPersons.length > 0 && (
              <>
                <AlertStrip
                  tone="danger"
                  title={`${dangerPersons.length} 位关键人存在健康风险缺口`}
                  sub={`${dangerPersons
                    .map((person) => `${person.code} ${person.title}`)
                    .join('、')} · 建议尽快补齐备份人选与决策权代行安排`}
                />
                <div className="sp12" />
              </>
            )}

            {keyPersons.map((person) => (
              <ListItem
                key={person.id}
                title={
                  <span>
                    {person.title}
                    <span className="mono-num" style={{ fontSize: 11, color: 'var(--muted-2)', marginLeft: 6 }}>
                      {person.code}
                    </span>
                  </span>
                }
                sub={
                  <span style={{ fontSize: 11.5 }}>
                    三年健康风险 {formatPct(person.risk)} · {person.plan}
                  </span>
                }
                right={
                  <ReadableBadge tone={KEY_PERSON_LEVEL[person.level].tone}>
                    {KEY_PERSON_LEVEL[person.level].text}
                  </ReadableBadge>
                }
              />
            ))}

            <div className="note-src" style={{ marginTop: 10 }}>
              <b>脱敏口径</b>　关键人一律以内部编号（如 KP-01）加职位显示，不展示任何私人姓名；风险值为模型估算，
              仅用于组织健康管理与风险预案排序。
            </div>
          </>
        )}
      </Card>

      <div className="note-src" style={{ marginTop: 12 }}>
        <b>口径与边界</b>　本页数据用于健康资本管理与风险研判，不构成诊断或投资建议；企业健康度由 HEW³ 模型依据
        成员授权的数据测算，最终判断由人类专家确认。数据授权可随时撤回，每次调用全量留痕。
      </div>
    </div>
  );
}
